import { supabase } from '../config/supabase';

const DEV = import.meta.env.DEV;

/**
 * Ghost Rally — admin-only service (Phase 1).
 * RLS enforces gating server-side; this layer is a thin CRUD wrapper.
 */

// Templates only — instances live with their event. Use the event detail
// or events list to reach an instance's dashboard.
export async function listGhostRallies(clubId) {
  const { data, error } = await supabase
    .from('ghost_rally_events')
    .select('*')
    .eq('club_id', clubId)
    .is('event_id', null)
    .order('updated_at', { ascending: false });
  if (error) {
    if (DEV) console.error('[GhostRally] list error:', error.message);
    return { data: null, error: error.message };
  }
  return { data, error: null };
}

export async function getGhostRally(rallyId) {
  const [{ data: rally, error: re }, { data: checkpoints, error: ce }] = await Promise.all([
    supabase.from('ghost_rally_events').select('*').eq('id', rallyId).single(),
    supabase.from('ghost_rally_checkpoints').select('*').eq('ghost_rally_event_id', rallyId).order('sort_order', { ascending: true }),
  ]);
  if (re) return { data: null, error: re.message };
  if (ce) return { data: null, error: ce.message };
  return { data: { ...rally, checkpoints: checkpoints || [] }, error: null };
}

export async function createGhostRally({ clubId, userId, fields }) {
  const row = {
    club_id: clubId,
    created_by: userId,
    title: fields.title || '',
    description: fields.description || null,
    start_location_label: fields.startLabel || null,
    start_location_lat: fields.startLat ?? null,
    start_location_lng: fields.startLng ?? null,
    finish_location_label: fields.finishLabel || null,
    finish_location_lat: fields.finishLat ?? null,
    finish_location_lng: fields.finishLng ?? null,
    finish_radius_feet: fields.finishRadiusFeet ?? 300,
    target_return_seconds: fields.targetReturnSeconds ?? null,
    max_duration_seconds: fields.maxDurationSeconds ?? null,
    checkpoint_order_type: fields.checkpointOrderType || 'open',
    bonus_intensity: fields.bonusIntensity || 'medium',
    status: 'draft',
  };
  const { data, error } = await supabase
    .from('ghost_rally_events')
    .insert(row)
    .select()
    .single();
  if (error) {
    if (DEV) console.error('[GhostRally] create error:', error.message);
    return { data: null, error: error.message };
  }
  return { data, error: null };
}

export async function updateGhostRally(rallyId, fields) {
  const row = {};
  const map = {
    title: 'title',
    description: 'description',
    startLabel: 'start_location_label',
    startLat: 'start_location_lat',
    startLng: 'start_location_lng',
    finishLabel: 'finish_location_label',
    finishLat: 'finish_location_lat',
    finishLng: 'finish_location_lng',
    finishRadiusFeet: 'finish_radius_feet',
    targetReturnSeconds: 'target_return_seconds',
    maxDurationSeconds: 'max_duration_seconds',
    checkpointOrderType: 'checkpoint_order_type',
    bonusIntensity: 'bonus_intensity',
    status: 'status',
  };
  for (const [k, col] of Object.entries(map)) {
    if (k in fields) row[col] = fields[k];
  }
  const { data, error } = await supabase
    .from('ghost_rally_events')
    .update(row)
    .eq('id', rallyId)
    .select()
    .single();
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function deleteGhostRally(rallyId) {
  const { error } = await supabase.from('ghost_rally_events').delete().eq('id', rallyId);
  if (error) return { error: error.message };
  return { error: null };
}

/* ─── Checkpoints ─── */

export async function addCheckpoint(rallyId, { name, description, latitude, longitude, radiusFeet, sortOrder }) {
  const row = {
    ghost_rally_event_id: rallyId,
    name: name || '',
    description: description || null,
    latitude,
    longitude,
    radius_feet: radiusFeet ?? 300,
    sort_order: sortOrder ?? 0,
  };
  const { data, error } = await supabase
    .from('ghost_rally_checkpoints')
    .insert(row)
    .select()
    .single();
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function updateCheckpoint(checkpointId, fields) {
  const row = {};
  const map = {
    name: 'name',
    description: 'description',
    latitude: 'latitude',
    longitude: 'longitude',
    radiusFeet: 'radius_feet',
    sortOrder: 'sort_order',
  };
  for (const [k, col] of Object.entries(map)) {
    if (k in fields) row[col] = fields[k];
  }
  const { data, error } = await supabase
    .from('ghost_rally_checkpoints')
    .update(row)
    .eq('id', checkpointId)
    .select()
    .single();
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function deleteCheckpoint(checkpointId) {
  const { error } = await supabase.from('ghost_rally_checkpoints').delete().eq('id', checkpointId);
  if (error) return { error: error.message };
  return { error: null };
}

/* ─── Definitions vs instances (Phase 2.5) ─── */

// Rally templates — drafts with no event_id yet. Used by the event creation
// route picker.
export async function listGhostRallyDefinitions(clubId) {
  const { data, error } = await supabase.rpc('list_ghost_rally_definitions', { p_club_id: clubId });
  if (error) return { data: null, error: error.message };
  return { data: data || [], error: null };
}

// Clone a definition's checkpoints into a per-event copy, link to club_event,
// flip status to 'armed'. Returns the new instance id.
export async function cloneGhostRallyForEvent(definitionId, eventId) {
  const { data, error } = await supabase.rpc('clone_ghost_rally_for_event', {
    p_definition_id: definitionId,
    p_event_id: eventId,
  });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

/* ─── Drivers + control (Phase 2) ─── */

export async function listRallyDrivers(rallyId) {
  const { data, error } = await supabase.rpc('get_rally_drivers', { p_rally_id: rallyId });
  if (error) return { data: null, error: error.message };
  return { data: data || [], error: null };
}

export async function setDriverCheckedIn(rallyId, userId, checkedIn) {
  const { error } = await supabase.rpc('set_driver_checked_in', {
    p_rally_id: rallyId,
    p_user_id: userId,
    p_checked_in: checkedIn,
  });
  if (error) return { error: error.message };
  return { error: null };
}

export async function cancelGhostRally(rallyId) {
  return updateGhostRally(rallyId, { status: 'cancelled' });
}

/* ─── Driver-facing (Phase 2) ─── */

export async function getRallyContextForEvent(eventId) {
  const { data, error } = await supabase.rpc('get_ghost_rally_for_event', { p_event_id: eventId });
  if (error) {
    if (DEV) console.error('[GhostRally] context error:', error.message);
    return { data: null, error: error.message };
  }
  return { data: data || null, error: null };
}

export async function registerForGhostRally(eventId, vehicleId = null) {
  const { data, error } = await supabase.rpc('register_for_ghost_rally', {
    p_event_id: eventId,
    p_vehicle_id: vehicleId,
  });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function unregisterFromGhostRally(eventId) {
  const { data, error } = await supabase.rpc('unregister_from_ghost_rally', { p_event_id: eventId });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

/* ─── Live mode (Phase 3) ─── */

export async function startRally(rallyId) {
  const { data, error } = await supabase.rpc('start_rally', { p_rally_id: rallyId });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function startDriver(rallyId, userId) {
  const { data, error } = await supabase.rpc('start_driver', {
    p_rally_id: rallyId, p_user_id: userId,
  });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function finishDriverManually(rallyId, userId) {
  const { data, error } = await supabase.rpc('finish_driver', {
    p_rally_id: rallyId, p_user_id: userId,
  });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function endRally(rallyId) {
  const { data, error } = await supabase.rpc('end_rally', { p_rally_id: rallyId });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function claimCheckpoint(rallyId, checkpointId, lat, lng, accuracyMeters = null) {
  const { data, error } = await supabase.rpc('claim_checkpoint', {
    p_rally_id: rallyId, p_checkpoint_id: checkpointId, p_lat: lat, p_lng: lng,
    p_accuracy_m: accuracyMeters,
  });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function claimFinish(rallyId, lat, lng, accuracyMeters = null) {
  const { data, error } = await supabase.rpc('claim_finish', {
    p_rally_id: rallyId, p_lat: lat, p_lng: lng, p_accuracy_m: accuracyMeters,
  });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function updateMyRallyPosition(rallyId, lat, lng, { speed = null, heading = null, accuracy = null } = {}) {
  const { error } = await supabase.rpc('update_my_rally_position', {
    p_rally_id: rallyId, p_lat: lat, p_lng: lng, p_speed: speed, p_heading: heading, p_accuracy: accuracy,
  });
  if (error) return { error: error.message };
  return { error: null };
}

export async function getMyRallyState(eventId) {
  const { data, error } = await supabase.rpc('get_my_rally_state', { p_event_id: eventId });
  if (error) return { data: null, error: error.message };
  return { data: data || null, error: null };
}

export async function listRallyLiveDrivers(rallyId) {
  const { data, error } = await supabase.rpc('get_rally_live_drivers', { p_rally_id: rallyId });
  if (error) return { data: null, error: error.message };
  return { data: data || [], error: null };
}

export async function getRallyLeaderboard(rallyId) {
  const { data, error } = await supabase.rpc('get_rally_leaderboard', { p_rally_id: rallyId });
  if (error) return { data: null, error: error.message };
  return { data: data || [], error: null };
}

export async function applyPendingAttack(bonusId, targetUserId) {
  const { data, error } = await supabase.rpc('apply_pending_attack', {
    p_bonus_id: bonusId,
    p_target_user_id: targetUserId,
  });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function getRallyOtherDriverPositions(rallyId) {
  const { data, error } = await supabase.rpc('get_rally_other_driver_positions', { p_rally_id: rallyId });
  if (error) return { data: null, error: error.message };
  return { data: data || [], error: null };
}

export async function getRallyBonusLog(rallyId) {
  const { data, error } = await supabase.rpc('get_rally_bonus_log', { p_rally_id: rallyId });
  if (error) return { data: null, error: error.message };
  return { data: data || [], error: null };
}

export async function getMyActiveRally() {
  const { data, error } = await supabase.rpc('get_my_active_rally');
  if (error) {
    if (DEV) console.error('[GhostRally] my_active_rally error:', error.message);
    return { data: null, error: error.message };
  }
  return { data: data || null, error: null };
}
